import React, { useState, useEffect, useRef } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu as MenuIcon, X } from "lucide-react";
import PageLayout from "../components/layout/PageLayout";
import { useCompany } from "../Context/CompanyContext";
import { useUser } from "../Context/UserContext";
import { cn } from "../lib/utils";

const navItems = [
  { path: "dashboard", label: "Dashboard" },
  { path: "identificacion", label: "Identificación" },
  { path: "descripcion", label: "Descripción" },
  { path: "info-empresa", label: "Información de empresa" },
  { path: "contacto", label: "Datos de Contacto" },
  { path: "servicios", label: "Servicios Ofrecidos" },
  { path: "zonas", label: "Zonas de Operación" },
  { path: "usuarios", label: "Usuarios" },
];

const CompanyAdmin = () => {
  const { companies } = useCompany();
  const navigate = useNavigate();
  const { user } = useUser();
  const location = useLocation();
  const [editFormData, setEditFormData] = useState({});
  const [isSidebarVisible, setIsSidebarVisible] = useState(false); // Nuevo estado para controlar la visibilidad del sidebar
  const sidebarRef = useRef(null);

  useEffect(() => {
    if (companies?.company) {
      setEditFormData(companies);
    }
  }, [companies]);

  // Redirigir al login si no hay usuario autenticado
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsSidebarVisible(false);
      }
    };

    if (isSidebarVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSidebarVisible]);

  if (!companies?.company && user) {
    return (
      <PageLayout>
        <div className="px-4 py-16 text-center text-muted-foreground">Cargando datos de la empresa...</div>
      </PageLayout>
    );
  }

  const id_company = companies?.company?.id_company;

  const isActive = (path) =>
    location.pathname === `/company-profile/${id_company}/admin/${path}`;

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);
  const closeSidebar = () => setIsSidebarVisible(false);

  return (
    <PageLayout>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <button
          onClick={toggleSidebar}
          className="mb-4 flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm md:hidden"
        >
          {isSidebarVisible ? <X className="h-4 w-4" /> : <MenuIcon className="h-4 w-4" />}
          {isSidebarVisible ? "Cerrar menú" : "Abrir menú"}
        </button>

        <div className="flex flex-col gap-6 md:flex-row">
          <nav
            ref={sidebarRef}
            className={cn("w-full shrink-0 md:block md:w-56", isSidebarVisible ? "block" : "hidden")}
          >
            <a
              href={`/company-profile/${editFormData.company?.id_company}/${editFormData.company?.nombre_empresa}`}
              className="mb-3 block truncate font-semibold text-foreground hover:text-primary"
            >
              {editFormData.company?.nombre_empresa?.length > 12
                ? `${editFormData.company.nombre_empresa.slice(0, 12)}...`
                : editFormData.company?.nombre_empresa || "Nombre de la Empresa"}
            </a>

            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path} onClick={closeSidebar}>
                  <Link
                    to={`/company-profile/${id_company}/admin/${item.path}`}
                    className={cn(
                      "block rounded-md px-3 py-2 text-sm",
                      isActive(item.path)
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted"
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex-1">
            <Outlet />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default CompanyAdmin;
